import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogBody,
  DialogHeader,
  Flex,
  Label,
  Progress,
  Text,
} from '@/components/ui';
import {
  FileProgressItem,
  FileProgressList,
} from '@/components/file-progress-list';
import { BulkDeleteConfirmationModalProps } from './types';

export const BulkDeleteConfirmationModal: React.FC<
  BulkDeleteConfirmationModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  files,
  isDeleting = false,
  currentDeletingFile = null,
  progress,
}) => {
  const progressPercentage =
    progress && progress.total > 0
      ? Math.round((progress.current / progress.total) * 100)
      : 0;

  // Convert CsvFile[] to FileProgressItem[]
  const fileProgressItems: FileProgressItem[] = useMemo(() => {
    if (!isDeleting) {
      // Before deletion, show all files as pending
      return files.map(file => ({
        id: file.id,
        name: file.filename,
        status: 'pending' as const,
        progress: 0,
      }));
    }

    // During deletion, show progress
    return files.map((file, index) => {
      const isCurrent = currentDeletingFile?.id === file.id;
      const isCompleted = progress && index < progress.current;

      let status: FileProgressItem['status'] = 'pending';
      let fileProgress = 0;

      if (isCompleted) {
        status = 'completed';
        fileProgress = 100;
      } else if (isCurrent) {
        status = 'processing';
        // Calculate progress based on current/total
        fileProgress = progress
          ? Math.round((progress.current / progress.total) * 100)
          : 0;
      }

      return {
        id: file.id,
        name: file.filename,
        status,
        progress: fileProgress,
      };
    });
  }, [files, isDeleting, currentDeletingFile, progress]);

  const currentProcessingIndex = useMemo(() => {
    if (!isDeleting || !currentDeletingFile) return undefined;
    return files.findIndex(f => f.id === currentDeletingFile.id);
  }, [files, isDeleting, currentDeletingFile]);

  const handleOpenChange = (open: boolean) => {
    // Prevent closing modal during deletion process
    if (!open && isDeleting) {
      return;
    }
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content size="large">
        <DialogHeader title="Delete CSV Files" />
        <DialogBody>
          {!isDeleting ? (
            <>
              <Text size="xsmall">
                Are you sure you want to delete <strong>{files.length}</strong>{' '}
                file
                {files.length !== 1 ? 's' : ''}? This action cannot be undone.
              </Text>
              {files.length > 0 && (
                <Box mt="4">
                  <Label>Files to delete:</Label>
                  <FileProgressList
                    files={fileProgressItems}
                    isProcessing={false}
                  />
                </Box>
              )}
            </>
          ) : (
            <Flex direction="column" gap="4">
              <Text size="2">
                Deleting files... Please wait while we process your request.
              </Text>
              {progress && (
                <Alert size="small" color="error">
                  <Flex justify="between" align="center">
                    <Text size="2xsmall" color="gray">
                      Progress: {progress.current} of {progress.total}
                    </Text>
                    <Text size="2xsmall" weight="medium">
                      {progressPercentage}%
                    </Text>
                  </Flex>
                  <Progress value={progressPercentage} />
                </Alert>
              )}
              <Box mt="2">
                <Box mb="2">
                  <Label>Files:</Label>
                </Box>
                <FileProgressList
                  files={fileProgressItems}
                  currentProcessingIndex={currentProcessingIndex}
                  isProcessing={isDeleting}
                />
              </Box>
            </Flex>
          )}
        </DialogBody>
        <Dialog.Footer>
          {!isDeleting ? (
            <>
              <Button
                variant="outlined"
                color="secondary"
                onClick={onClose}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="classic"
                color="error"
                onClick={onConfirm}
                disabled={isDeleting}
              >
                Delete {files.length} file{files.length !== 1 ? 's' : ''}
              </Button>
            </>
          ) : (
            <Button variant="outlined" color="secondary" disabled>
              Deleting...
            </Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
